import { has, isEmpty, isFunction } from 'lodash'
import { inject, nextTick, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { AZURE_DEVOPS_SDK } from '@/utils/azure-devops-helpers/general'
import {
  composeUrlSegments,
  createQueryForExternalLink,
  getDefaultAppRoute,
  getHashFromLocation,
  replaceAzureLocationHash,
  replaceForgeLocationHash
} from '@/utils/external-link-helper'
import { forgeView } from '@/utils/forge-helpers/general'
import { copyToClipboard } from '@/utils/general'
import {
  isAzureAppInjectionKey,
  isCrossPlatformAppInjectionKey,
  isJiraAppInjectionKey,
  isWebAppInjectionKey
} from '@/utils/injection-keys'
import { NOTIFICATION_DURATIONS, showNotify } from '@/utils/notify'
import { OKR_FORM_VIEWS } from '@/utils/objectives'
import { getAtlassianBaseUrl } from '@jira/util'

export const createdObjectiveModalNotificationActions = {
  OPEN: {
    id: 0,
    label: 'action.open',
    closeOnHandle: true
  },
  COPY_LINK: {
    id: 1,
    label: 'breadcrumb.copy_link',
    closeOnHandle: true,
    handler: async ({
      workspaceId,
      createdElement,
      isJiraApp = true,
      isWebApp = false,
      isAzureApp = false,
      preferredPath = ''
    } = {}) => {
      if (!String(workspaceId)) {
        throw new Error('workspaceId is required')
      }

      let baseUrl = getAtlassianBaseUrl()

      if (isWebApp) {
        baseUrl = `${location.origin}/${getHashFromLocation()}`
      }

      const composedQueryParameter = createQueryForExternalLink({
        ...createdElement,
        isJiraApp
      })

      const DEFAULT_APP_ROUTE = getDefaultAppRoute({
        workspaceId: workspaceId,
        query: composedQueryParameter
      })

      const APP_ROUTE = preferredPath
        ? `${preferredPath}${composedQueryParameter}`
        : DEFAULT_APP_ROUTE

      let url = ''

      if (forgeView) {
        url = await replaceForgeLocationHash({
          hash: APP_ROUTE
        })
      } else if (isAzureApp && AZURE_DEVOPS_SDK) {
        url = await replaceAzureLocationHash({
          hash: APP_ROUTE
        })
      } else {
        url = composeUrlSegments(baseUrl, APP_ROUTE)
      }

      if (!url) return

      copyToClipboard(url)
    }
  }
}

export const getCreatedElementNotificationTitle = elementType => {
  const { KR, OBJECTIVE, LINK_JIRA_ISSUE, LINK_WORK_ITEM } = OKR_FORM_VIEWS

  const titles = {
    [KR]: 'create.keyResult.success_message',
    [LINK_JIRA_ISSUE]: 'create.task.success_message',
    [LINK_WORK_ITEM]: 'create.work_item.success_message',
    [OBJECTIVE]: 'create.objective.success_title'
  }

  return titles[elementType] || 'create.objective.success_title'
}

export const checkIsShowNotification = eventData => {
  return {
    OBJECTIVE:
      has(eventData, 'isChildModal') &&
      !eventData.isChildModal &&
      !isEmpty(eventData.createdObjective),
    KR: !isEmpty(eventData.createdKR),
    LINK_JIRA_ISSUE: !isEmpty(eventData.createdTasks),
    LINK_WORK_ITEM: !isEmpty(eventData.createdWorkItems)
  }
}

/**
 * Notifications of the objective modal only: what a page does besides them stays in the page
 * @param {object} [options]
 * @param {import('vue').MaybeRefOrGetter<boolean>} [options.isModalOpen] whether the objective modal of the page is open
 * @param {import('vue').MaybeRefOrGetter<string>} [options.preferredPath] path the copied link leads to, the okr table is used without it
 */
export const useObjectiveModalNotifications = ({
  isModalOpen = false,
  preferredPath = ''
} = {}) => {
  const { t } = useI18n()
  const store = useStore()

  const isJiraApp = inject(isJiraAppInjectionKey)
  const isWebApp = inject(isWebAppInjectionKey)
  const isCrossPlatformApp = inject(isCrossPlatformAppInjectionKey)
  const isAzureApp = inject(isAzureAppInjectionKey)

  /**
   * Notifies about the elements created by the time the modal is closed
   * @param {object} eventData payload of the modal `close` event
   * @param {object} handlers
   * @param {Function} handlers.onOpen opens the created element on the page
   */
  const showModalCloseNotifications = (eventData, { onOpen } = {}) => {
    if (!isFunction(onOpen)) {
      throw new Error('onOpen is required and must be a function')
    }

    const isNeedShowNotification = checkIsShowNotification(eventData)
    const notifications = []

    let title = null
    let createdElement = null
    if (isNeedShowNotification.OBJECTIVE) {
      createdElement = eventData.createdObjective
      title = 'create.objective.success_title'
    }

    if (isNeedShowNotification.KR) {
      createdElement = eventData.createdKR
      title = 'create.keyResult.success_message'
    }

    if (title && createdElement) {
      notifications.push({
        createdElement,
        title,
        content: 'create.objective.success_description'
      })
    }

    if (isNeedShowNotification.LINK_JIRA_ISSUE) {
      eventData.createdTasks.forEach(createdTask => {
        notifications.push({
          createdElement: createdTask,
          title: 'create.task.success_message'
        })
      })
    }

    if (isNeedShowNotification.LINK_WORK_ITEM) {
      eventData.createdWorkItems.forEach(createdWorkItem => {
        notifications.push({
          createdElement: createdWorkItem,
          title: 'create.work_item.success_message'
        })
      })
    }

    notifications.forEach(notification => {
      const notifyOptions = {
        duration: NOTIFICATION_DURATIONS.SHORT,
        expanded: true,
        title: t(notification.title),
        actions: [
          {
            ...createdObjectiveModalNotificationActions.OPEN,
            handler: () => {
              onOpen(notification.createdElement)
            }
          },
          {
            ...createdObjectiveModalNotificationActions.COPY_LINK,
            handler: () => {
              if (isEmpty(notification.createdElement)) {
                throw new Error('createdElement is empty')
              }

              showNotify({ title: t('notifications.link_copied') })

              // the notification above does not wait for the link, the same as before
              return createdObjectiveModalNotificationActions.COPY_LINK.handler({
                workspaceId: store.state.workspaces.workspaceId,
                createdElement: notification.createdElement,
                isJiraApp,
                isWebApp,
                isAzureApp,
                preferredPath: toValue(preferredPath)
              })
            },
            shouldBeRemoved: isCrossPlatformApp // force remove copy link action for cross-platform app
          }
        ].filter(action => !action.shouldBeRemoved)
      }

      // linked issues and work items are notified without a description
      if (notification.content) {
        notifyOptions.content = t(notification.content)
      }

      showNotify(notifyOptions)
    })
  }

  /**
   * Notifies about an element created while the modal stays open
   * @param {number} elementType form view the element is created in
   */
  const showElementCreatedNotification = elementType => {
    const title = getCreatedElementNotificationTitle(elementType)

    setTimeout(async () => {
      // wait for modal to close
      await nextTick()

      if (toValue(isModalOpen)) {
        // show notify only if objective modal is open
        // that means that user created (KR, TASK, OBJECTIVE) from objective modal
        showNotify({
          title: t(title)
        })
      }
    })
  }

  return {
    showModalCloseNotifications,
    showElementCreatedNotification
  }
}

export const NOTIFICATION_CHANNEL_ATTRIBUTES_TYPE_IDS = {
  SLACK_WEBHOOKS: 1,
  TEAMS_WEBHOOKS: 2,
  DISCORD_WEBHOOKS: 3
}

export const NOTIFICATION_CHANNELS = {
  INBOX: {
    typeId: 1,
    attributes: {},
    guideLink: null,
    title: 'Inbox'
  },
  EMAIL: {
    typeId: 2,
    attributes: {},
    guideLink: null,
    title: 'Email'
  },
  SLACK: {
    typeId: 3,
    attributes: {
      [NOTIFICATION_CHANNEL_ATTRIBUTES_TYPE_IDS.SLACK_WEBHOOKS]: {
        isWebHook: true,
        defaultValue: [],
        uid: 'tjuee672b'
      }
    },
    guideLink: 'https://oboard.atlassian.net/wiki/spaces/OBPFJ/pages/1467219975',
    icon: 'connection-slack',
    title: 'Slack'
  },
  TEAMS: {
    typeId: 4,
    attributes: {
      [NOTIFICATION_CHANNEL_ATTRIBUTES_TYPE_IDS.TEAMS_WEBHOOKS]: {
        isWebHook: true,
        defaultValue: [],
        uid: 'a5k65kvl4'
      }
    },
    guideLink: 'https://oboard.atlassian.net/wiki/spaces/OBPFJ/pages/1467318278',
    icon: 'connection-teams',
    title: 'Teams'
  },
  DISCORD: {
    typeId: 5,
    attributes: {
      [NOTIFICATION_CHANNEL_ATTRIBUTES_TYPE_IDS.DISCORD_WEBHOOKS]: {
        isWebHook: true,
        defaultValue: [],
        uid: 'l4che2wrq'
      }
    },
    guideLink: 'https://oboard.atlassian.net/wiki/spaces/OBPFJ/pages/1467809798',
    icon: 'connection-discord',
    title: 'Discord'
  }
}

export const NOTIFICATIONS_CHANNELS_BY_TYPE_ID = Object.values(NOTIFICATION_CHANNELS).reduce(
  (acc, channel) => {
    return {
      ...acc,
      [channel.typeId]: channel
    }
  },
  {}
)
